import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import postService from '../services/postService';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { parseSocietyUrl, getPostUrl } from '../utils/urlHelpers';
import { formatDate } from '../utils/helpers';
// Removed PostCard usage for custom magazine layout

/** Public URL for post thumbnails when the post has no image (served from `public/images/`). */
const POST_IMAGE_PLACEHOLDER = '/images/pakpoints-placeholder.png';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const PostsPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { showError } = useToast();
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  
  // Check if this is a society-specific URL
  const { isSocietySpecific, societySlug } = parseSocietyUrl(location.pathname);
  
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    sortBy: '-created_at'
  });
  const [activeTab, setActiveTab] = useState('latest');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getPostImageUrl = (p) => {
    if (!p) return null;
    if (p.images && p.images.length > 0 && p.images[0]?.original_image) return p.images[0].original_image;
    return p.image || p.thumbnail || p.cover_image || null;
  };

  const getPostImageSrc = (p) => getPostImageUrl(p) || POST_IMAGE_PLACEHOLDER;

  const handlePostImageError = (e) => {
    e.target.onerror = null;
    e.target.src = POST_IMAGE_PLACEHOLDER;
  };

  const loadPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        page_size: 12,
        search: debouncedSearchTerm || undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        ordering: '-created_at', // Always sort by newest first
      };

      // Add society filter if this is a society-specific page
      if (isSocietySpecific && societySlug) {
        params.society = societySlug;
      }

      const response = await postService.getPosts(params);
      const newPosts = response.results || response;

      if (append) {
        setPosts(prev => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(!!response.next);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Failed to load posts. Please try again.');
      showError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm, filters, isSocietySpecific, societySlug, showError]);

  // Load posts when filters or search term changes
  useEffect(() => {
    loadPosts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearchTerm]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      loadPosts(page + 1, true);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    if (tabValue === 'latest') {
      setFilters({ category: 'all', sortBy: '-created_at' });
      return;
    }
    // Map tabs to categories
    const tabToCategory = {
      design: 'lifestyle',
      development: 'technology',
      management: 'business',
      marketing: 'business',
    };
    const newCategory = tabToCategory[tabValue] || 'all';
    setFilters(prev => ({ ...prev, category: newCategory }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      news: 'bg-blue-100 text-blue-800',
      technology: 'bg-purple-100 text-purple-800',
      business: 'bg-green-100 text-green-800',
      health: 'bg-red-100 text-red-800',
      lifestyle: 'bg-pink-100 text-pink-800',
      entertainment: 'bg-yellow-100 text-yellow-800',
      sports: 'bg-orange-100 text-orange-800',
      education: 'bg-indigo-100 text-indigo-800',
      opinion: 'bg-teal-100 text-teal-800',
      qa: 'bg-cyan-100 text-cyan-800',
      travel: 'bg-emerald-100 text-emerald-800',
      visa: 'bg-sky-100 text-sky-800',
      recipes: 'bg-amber-100 text-amber-800',
      announcements: 'bg-violet-100 text-violet-800',
      local: 'bg-lime-100 text-lime-800',
      deals: 'bg-rose-100 text-rose-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[category] || colors.other;
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'news', label: 'News / Current Events' },
    { value: 'technology', label: 'Technology / Gadgets' },
    { value: 'business', label: 'Business / Economy' },
    { value: 'health', label: 'Health / Wellness' },
    { value: 'lifestyle', label: 'Lifestyle / Culture' },
    { value: 'entertainment', label: 'Entertainment / Media' },
    { value: 'sports', label: 'Sports' },
    { value: 'education', label: 'Education / Career' },
    { value: 'opinion', label: 'Opinion / Editorials' },
    { value: 'qa', label: 'Ask & Answer / Q&A' },
    { value: 'travel', label: 'Points / Places' },
    { value: 'visa', label: 'Visa / Immigration' },
    { value: 'recipes', label: 'Recipes / Food' },
    { value: 'announcements', label: 'Announcements / Updates' },
    { value: 'local', label: 'Local / Regional Topics' },
    { value: 'deals', label: 'Deals / Offers' },
    { value: 'other', label: 'Other' },
  ];

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <Helmet>
          <title>Posts - PakPoints</title>
          <meta name="description" content="Browse community posts, announcements, and discussions on PakPoints" />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>Posts - PakPoints</title>
        <meta name="description" content="Browse community posts, announcements, and discussions on PakPoints" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-10">
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900">
                Posts
              </h1>
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/posts/create')}
                  className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                >
                  Create Post
                </button>
              )}
            </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Error loading posts
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <p>{error}</p>
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={() => loadPosts(1, false)}
                            className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                          >
                            Try again
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

            {/* Magazine Layout - Helper: minimal article tile for grid items */}

            {!error && posts && posts.length > 0 ? (
              <>
                {/* Featured block (image left, text right) */}
                {posts[0] && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-12">
                    <button onClick={() => navigate(getPostUrl(posts[0], societySlug))} className="block group">
                      <img
                        src={getPostImageSrc(posts[0])}
                        alt={posts[0].title}
                        className="w-full h-80 sm:h-96 object-cover rounded bg-gray-100"
                        onError={handlePostImageError}
                      />
                    </button>
                    <div>
                      <button onClick={() => navigate(getPostUrl(posts[0], societySlug))} className="text-left group">
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 group-hover:underline">
                          {posts[0].title}
                        </h2>
                        <p className="mt-4 text-lg text-gray-700 max-w-2xl">{posts[0].summary || posts[0].description}</p>
                      </button>
                      {/* Meta row with simple progress/time */}
                      <div className="mt-6 flex items-center space-x-6 text-sm text-gray-600">
                        <span>Post Reports / Podcast</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-28 h-2 bg-gray-200 rounded">
                            <div className="h-2 bg-gray-900 rounded w-3/5"></div>
                          </div>
                          <span>23:45</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-200 mb-8"></div>

                {/* Remaining posts grid - minimal tiles */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-14">
                  {posts.slice(1).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(getPostUrl(p, societySlug))}
                      className="text-left group"
                    >
                      <img
                        src={getPostImageSrc(p)}
                        alt={p.title}
                        className="w-full h-56 object-cover rounded bg-gray-100"
                        onError={handlePostImageError}
                      />
                      <h3 className="mt-4 text-2xl font-semibold text-gray-900 group-hover:underline">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-base text-gray-600 line-clamp-3">
                        {p.summary || p.description}
                      </p>
                      <div className="mt-4 border-t border-gray-200"></div>
                    </button>
                  ))}
                </div>
              </>
            ) : !error ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
                    <p className="text-gray-500 mb-4">
                      {debouncedSearchTerm 
                        ? `No posts found matching "${debouncedSearchTerm}"`
                        : 'There are no posts available at the moment.'
                      }
                    </p>
                    {debouncedSearchTerm && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setFilters(prev => ({ ...prev, search: '' }));
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : null}
            {/* Load More Button */}
            {!error && hasMore && posts.length > 0 && (
              <div className="mt-10 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading...' : 'Load More Posts'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PostsPage;